import { Component, ElementRef, HostBinding, inject, NgZone, OnInit, ViewChild } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { ApplicationService } from '@core/application/application.service';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { EVENT_MANAGER_CONSTANTS } from '@core/events-manager/events-manager.constants';
import { EventsManagerService } from '@core/events-manager/events-manager.service';
import { FeatureModificationService } from '@core/feature/feature-modification.service';
import { FileService } from '@core/file/file.service';
import { InventoryService } from '@core/inventory/inventory.service';
import { HIT_PMS_HTML_EVENTS } from '@core/legacy/hit-pms-html.constants';
import { LegacyEventService } from '@core/legacy/legacy-event.service';
import { LegacyNavigationService } from '@core/legacy/legacy-navigation.service';
import { LockManagerService } from '@core/lock-manager/lock-manager.service';
import { DialstackWidgetService } from '@core/phone-call/dialstack-widget.service';
import { _pullAll } from '@core/lodash/lodash';
import { DialogUtil, ModalManagerService, TooltipService, TypeSafeModalManagerService } from 'morgana';
import { PrintService } from '@core/print/print.service';
import { StateManagementService } from '@core/state-management/state-management.service';
import { ToasterService } from '@core/toaster/toaster.service';
import { UrlParamService } from '@core/url-util/url-param.service';
import { LetterEditorDockableModalComponent } from '@shared/component/letter-editor-dockable-modal/letter-editor-dockable-modal.component';
import { LetterEditorDockableModalService } from '@shared/component/letter-editor-dockable-modal/letter-editor-dockable-modal.service';
import { RapidReviewModalService } from '@shared/component/rapid-review-modal/rapid-review-modal.service';
import { RapidReviewModalComponent, RapidReviewModalData } from '@shared/component/rapid-review-modal/rapid-review-modal.component';
import { TaskService } from '@core/task/task.service';
import { AdminFeatureSubscriptionService } from '@admin/services/admin-feature-subscription.service';
import { EditProfileSubscriptionService } from '@header/services/edit-profile-subscription.service';
import { ManageUpdatesSubscriptionService } from '@header/services/manage-updates-subscription.service';
import { OrdersEventService } from '@orders/order/orders-event.service';
import { PatientManagerService } from '@patients/core/patient-manager/patient-manager.service';
import { TodaysPatientsDockableModalComponent } from '@header/todays-patients-dockable-modal/todays-patients-dockable-modal.component';
import { NavigationMenuService } from '@header/services/navigation-menu.service';
import { MajToastsContainerComponent } from 'majora';
import { AgreementGateService } from './agreements/agreement-gate.service';
import { FeatureFlagToastService } from './services/feature-flag-toast.service';
import { UserpilotService } from './services/userpilot.service';
import { LiveChatService } from './services/live-chat.service';
import { IframeContainerComponent } from './iframe-container/iframe-container.component';

export interface ChangeRouteEventData {
	argument: {
		primaryContext: string;
		subContexts: string[];
	};
	eventName: string;
}

@Component({
	selector: 'pms-root',
	templateUrl: './app.component.html',
	imports: [IframeContainerComponent, MajToastsContainerComponent, RouterOutlet],
	providers: [
		ModalManagerService,
		TypeSafeModalManagerService,
		{provide: 'todaysPatientsModalManagerService', useClass: TypeSafeModalManagerService},
		{provide: 'letterEditorModalManagerService', useClass: TypeSafeModalManagerService},
	],
})
export class AppComponent implements OnInit {
	private adminFeatureSubscriptionService = inject(AdminFeatureSubscriptionService);
	private featureFlagToastService = inject(FeatureFlagToastService);
	private featureModificationService = inject(FeatureModificationService);
	private authenticationService = inject(AuthenticationService);
	private manageUpdatesSubscriptionService = inject(ManageUpdatesSubscriptionService);
	private editProfileSubscriptionService = inject(EditProfileSubscriptionService);
	private toasterService = inject(ToasterService);
	private lockManagerService = inject(LockManagerService);
	private stateManagementService = inject(StateManagementService);
	private tooltipService = inject(TooltipService);
	private legacyEventService = inject(LegacyEventService);
	private eventsManagerService = inject(EventsManagerService);
	private legacyNavigationService = inject(LegacyNavigationService);
	private urlParamService = inject(UrlParamService);
	private patientManagerService = inject(PatientManagerService);
	private ordersEventService = inject(OrdersEventService);
	private applicationService = inject(ApplicationService);
	modalManagerService = inject(ModalManagerService);
	typeSafeModalManagerService = inject(TypeSafeModalManagerService);
	todaysPatientsModalManagerService = inject<TypeSafeModalManagerService>('todaysPatientsModalManagerService' as any);
	letterEditorModalManagerService = inject<TypeSafeModalManagerService>('letterEditorModalManagerService' as any);
	private zone = inject(NgZone);
	private router = inject(Router);
	private fileService = inject(FileService);
	private printService = inject(PrintService);
	private inventoryService = inject(InventoryService);
	private navigationMenuService = inject(NavigationMenuService);
	agreementGateService = inject(AgreementGateService);
	private rapidReviewModalService = inject(RapidReviewModalService);
	private letterEditorDockableModalService = inject(LetterEditorDockableModalService);
	private taskManagementService = inject(TaskService);
	private userpilotService = inject(UserpilotService);
	private liveChatService = inject(LiveChatService);
	private phoneWidgetService = inject(DialstackWidgetService);

	@HostBinding('class') hostClass = 'rev-modern';

	@ViewChild('printSection', {read: ElementRef, static: true})
	printSection: ElementRef;

	isTodaysPatientsDockableModalOpen = false;
	isLetterEditorDockableModalOpen = false;

	constructor() {
		this.featureModificationService.subscribeToRouteChanges();

		this.eventsManagerService.subscribe(HIT_PMS_HTML_EVENTS.APPLICATION.CHANGE_ROUTE, async (eventData: ChangeRouteEventData) => {
			if (eventData && eventData.argument) {
				let changeRoute = [eventData.argument.primaryContext];
				if (eventData.argument.subContexts && eventData.argument.subContexts.length !== 0) {
					changeRoute = changeRoute.concat(eventData.argument.subContexts);
					changeRoute = _pullAll(changeRoute, [undefined, null]);
				}
				// Hack - need to wait for the pms2 authentication to complete before navigating, hit-pms-html has no view into this
				// this wait should only happen on the first navigation (from the login button)
				if (!this.authenticationService.isAuthenticated.getValue()) {
					this.authenticationService.isAuthenticated.subscribe(async (isAuthenticated) => {
						// The legacy header shell fires this unconditionally on login (navigateToTopLevelModule('home')),
						// racing our own post-login navigation below. Don't let it steer away from an unresolved agreements gate.
						if (isAuthenticated && this.agreementGateService.resolved()) {
							await this.forceNavigation(changeRoute);
						}
					});
				} else if (this.agreementGateService.resolved()) {
					await this.forceNavigation(changeRoute);
				}
			}
		});

		this.authenticationService.subscribeToSessionEvents();
		this.adminFeatureSubscriptionService.subscribeToFeatureEvent();
		this.manageUpdatesSubscriptionService.subscribeToFeatureEvent(this.typeSafeModalManagerService);
		this.editProfileSubscriptionService.subscribeToFeatureEvent(this.typeSafeModalManagerService);
		this.lockManagerService.subscribeToLockEvent();
		this.stateManagementService.subscribeToRouteChange();
		this.tooltipService.setGlobalDefaults();
		this.legacyEventService.subscribeToActionEvent();
		this.urlParamService.subscribeForUrlParams();
		this.ordersEventService.subscribeToEvents();
		this.applicationService.monitorApplicationStableState();
		this.inventoryService.subscribeToLegacyEvents();
		this.taskManagementService.subscribeToEvents();

		this.authenticationService.loggedIn.subscribe(() => this.loggedIn());
		this.authenticationService.loggedOut.subscribe(() => this.phoneWidgetService.stop());
	}

	ngOnInit() {
		this.subscribeForToasts();
		this.subscribeForLegacyClose();
		this.subscribeForPdfViewer();
		this.subscribeForImageViewer();
		this.setupPrintElement();
		this.subscribeToTodaysPatients();
		this.subscribeToLetterEditor();
		this.subscribeToAgreementAccepted();
		this.subscribeToRapidReviewModal();
		window['applicationReady'] = true;
	}

	setupPrintElement() {
		this.printService.setPrintSection(this.printSection);
	}

	subscribeForToasts() {
		this.featureModificationService.flagsToTurnOn.subscribe(flags => {
			const flagsOn = flags.join(', ');
			if (flagsOn) {
				this.toasterService.showSavedSuccess({
					title: 'Flag modification successful!',
					content: flagsOn,
				});
			}
		});
	}

	subscribeForLegacyClose() {
		this.eventsManagerService.subscribe(HIT_PMS_HTML_EVENTS.ADMINISTRATION.ADMIN_MODULE_HIDE_LEGACY, () => {
			this.legacyNavigationService.hideLegacy('admin');
		}, this);
	}

	subscribeForPdfViewer() {
		this.eventsManagerService.subscribe(EVENT_MANAGER_CONSTANTS.GLOBAL.PDF_VIEWER, (event: any) => {
			this.zone.run(() => this.fileService.openPdfViewerModal(event.argument.fileName, event.argument.fileUrl, event.argument.openPrint));
		}, this);
	}

	subscribeForImageViewer() {
		this.eventsManagerService.subscribe(EVENT_MANAGER_CONSTANTS.GLOBAL.IMAGE_VIEWER, (event: any) => {
			this.zone.run(() => this.fileService.openImagePreviewModal(event.argument.filePath));
		}, this);
	}

	subscribeToTodaysPatients() {
		this.eventsManagerService.subscribe(EVENT_MANAGER_CONSTANTS.HEADER.TODAYS_PATIENTS_OPEN, () => {
			if (this.isTodaysPatientsDockableModalOpen) {
				this.eventsManagerService.publish(EVENT_MANAGER_CONSTANTS.HEADER.TODAYS_PATIENTS_RESIZE);
			} else {
				this.openTodaysPatients();
			}
		});
	}

	openTodaysPatients() {
		this.isTodaysPatientsDockableModalOpen = true;
		this.todaysPatientsModalManagerService.open(TodaysPatientsDockableModalComponent, {}).onClose.subscribe(() => {
			this.isTodaysPatientsDockableModalOpen = false;
		});
	}

	subscribeToAgreementAccepted() {
		this.eventsManagerService.subscribe(HIT_PMS_HTML_EVENTS.AGREEMENTS.AGREEMENT_ACCEPTED, () => {
			this.agreementGateService.usingLegacyAgreementsScreen.set(false);
			this.featureFlagToastService.subscribe(this.typeSafeModalManagerService);
			this.patientManagerService.subscribeToLegacyEvents();
			this.navigationMenuService.subscribeToTwtUnreadConversationsInterval();
			this.navigationMenuService.subscribeToPendingPaymentCount();
			this.phoneWidgetService.start();
		});
	}

	private loggedIn() {
		this.userpilotService.initialize();
		this.liveChatService.initialize();

		if (this.agreementGateService.navigateIfAgreementOwed()) {
			return;
		}

		this.featureFlagToastService.subscribe(this.typeSafeModalManagerService);
		this.patientManagerService.subscribeToLegacyEvents();
		this.navigationMenuService.subscribeToTwtUnreadConversationsInterval();
		this.navigationMenuService.subscribeToPendingPaymentCount();
		this.phoneWidgetService.start();
	}

	subscribeToLetterEditor() {
		this.eventsManagerService.subscribe(EVENT_MANAGER_CONSTANTS.PATIENTS.LETTER_EDITOR, (data) => {
			this.openLetterEditorWithGuard(data.argument.patientDocumentId, data.argument.existingTask);
		});

		this.letterEditorDockableModalService.openRequest$.subscribe(request => {
			this.openLetterEditorWithGuard(request.patientDocumentId, request.existingTask);
		});
	}

	openLetterEditorWithGuard(patientDocumentId: number, existingTask: boolean) {
		if (this.isLetterEditorDockableModalOpen) {
			DialogUtil.alert({
				title: 'Write Letter Alert',
				content: 'Only one communication letter may be edited at a time. Please finish the letter already in progress and try again.',
				showCloseIcon: false,
			});
		} else {
			this.openLetterEditor(patientDocumentId, existingTask);
		}
	}

	openLetterEditor(patientDocumentId: number, existingTask: boolean) {
		this.isLetterEditorDockableModalOpen = true;
		this.letterEditorModalManagerService.open(LetterEditorDockableModalComponent, {patientDocumentId, existingTask}).onClose.subscribe(() => {
			this.isLetterEditorDockableModalOpen = false;
		});
	}

	subscribeToRapidReviewModal() {
		this.rapidReviewModalService.opened.subscribe(data => {
			if (typeof data === 'number') {
				data = {encounterId: data, isCurrentEncounter: true} as RapidReviewModalData;
			}
			this.typeSafeModalManagerService.open(RapidReviewModalComponent, data);
		});
	}

	forceNavigation(route: string[]) {
		return this.zone.run(() => this.router.navigate(route));
	}
}

